import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateUser, useDomains, useUpdateUser } from "@/lib/queries";
import {
  EXTENSION_RE, ROLE_DESCRIPTIONS, ROLE_LABELS, assignableRoles, editsOwnPhone, type AppUser,
  type Role,
} from "@/lib/types";
import { generatePassphrase } from "@/lib/passphrase";

/**
 * Create / edit a user, and with them the phone that is theirs.
 *
 * The scope fields are conditional on the role and mirror `rbac.valid_scope_for_role` on the
 * server: a superadmin has no tenant or domain, an admin has a tenant but no domain, and the
 * two lower roles need both. Showing a domain picker for an admin and then having the server
 * reject it is a worse experience than the picker simply not being there — so the form encodes
 * the rule rather than discovering it.
 *
 * The phone is here and not on a dialog of its own because it is not a separate thing any more:
 * an account is created with its endpoint and keeps it for life, so what is left to edit is the
 * extension and whether it records. There is no picker for *which* endpoint, deliberately — a
 * call leg names a rep by realm and number and by nothing else, so an endpoint that changed
 * hands would hand its new holder the last holder's call history (ADR-012 in `platform`).
 */

const MIN_PASSWORD = 12;

const schema = z
  .object({
    email: z.string().min(1, "Required").email("That does not look like an email"),
    display_name: z.string().max(200).optional(),
    role: z.enum(["superadmin", "admin", "supervisor", "rep"]),
    tenant_id: z.number().nullable(),
    domain_id: z.number().nullable(),
    /** Where an admin or superadmin's phone goes. Null for the domain-locked roles, whose phone
     *  lives in `domain_id`. */
    endpoint_domain_id: z.number().nullable(),
    extension: z.string(),
    record_calls: z.boolean(),
    is_active: z.boolean(),
    password: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.role !== "superadmin" && value.role !== "admin" && value.domain_id === null) {
      ctx.addIssue({
        code: "custom",
        path: ["domain_id"],
        message: `A ${ROLE_LABELS[value.role].toLowerCase()} must be locked to a domain`,
      });
    }
    if (value.extension && !EXTENSION_RE.test(value.extension)) {
      ctx.addIssue({
        code: "custom",
        path: ["extension"],
        message: "Exactly four digits, for example 1004",
      });
    }
    if (value.password && value.password.length < MIN_PASSWORD) {
      ctx.addIssue({
        code: "custom",
        path: ["password"],
        message: `At least ${MIN_PASSWORD} characters`,
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export function UserFormDialog({
  open, onOpenChange, user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  user?: AppUser;
}) {
  const editing = Boolean(user);
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateUser();
  const update = useUpdateUser();
  const { data: domains } = useDomains();
  const [formError, setFormError] = React.useState<string | null>(null);

  const roles = assignableRoles(actor?.role);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "", display_name: "", role: (roles.at(-1) ?? "rep") as Role,
      tenant_id: actor?.tenant_id ?? null, domain_id: null, endpoint_domain_id: null,
      extension: "", record_calls: true, is_active: true, password: "",
    },
  });
  // Measured from what the reset below loads, so a new user's generated password is not a change.
  const changed = useFormChanged(form);

  const role = form.watch("role");
  const needsDomain = role === "supervisor" || role === "rep";
  const hasPhone = Boolean(user?.sip_endpoint_id);
  // Which realm this account's phone lives in: their own for the domain-locked roles, the one
  // chosen for it otherwise. Null means there is nowhere to put a phone yet, and the extension
  // field would have nothing to name.
  const phoneDomainId = needsDomain
    ? form.watch("domain_id")
    : form.watch("endpoint_domain_id");
  const canHavePhone = hasPhone || phoneDomainId !== null;

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      user
        ? {
            email: user.email,
            display_name: user.display_name ?? "",
            role: user.role,
            tenant_id: user.tenant_id,
            domain_id: user.domain_id,
            endpoint_domain_id: null,
            extension: user.sip_endpoint_extension ?? "",
            record_calls: user.sip_endpoint_record_calls ?? true,
            is_active: user.is_active,
            password: "",
          }
        : {
            email: "", display_name: "", role: (roles.at(-1) ?? "rep") as Role,
            tenant_id: actor?.tenant_id ?? null, domain_id: null, endpoint_domain_id: null,
            extension: "", record_calls: true, is_active: true,
            password: generatePassphrase(),
          },
    );
    // `roles` is derived from the actor and stable for a session; including it would reset the
    // form on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, actor?.tenant_id]);

  // Clear a domain that the new role must not carry, so a promotion cannot submit a scope the
  // server will reject.
  React.useEffect(() => {
    if (!needsDomain && form.getValues("domain_id") !== null) form.setValue("domain_id", null);
  }, [needsDomain, form]);

  const renaming = Boolean(
    hasPhone && form.watch("extension") && form.watch("extension") !== user?.sip_endpoint_extension,
  );

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const extension = values.extension.trim();
    const payload = {
      email: values.email,
      display_name: values.display_name?.trim() || null,
      role: values.role,
      tenant_id: values.tenant_id,
      domain_id: values.domain_id,
      is_active: values.is_active,
      // Only when there is somewhere for a phone to be. Sent without one the server refuses,
      // correctly — it has no realm to create the endpoint in.
      ...(canHavePhone && extension ? { extension } : {}),
      ...(!needsDomain && values.endpoint_domain_id !== null
        ? { endpoint_domain_id: values.endpoint_domain_id }
        : {}),
      ...(hasPhone ? { record_calls: values.record_calls } : {}),
      ...(values.password ? { password: values.password } : {}),
    };
    try {
      if (user) {
        await update.mutateAsync({ id: user.id, ...payload });
        toast.success("User updated", values.email);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success(
          "User created",
          created.sip_endpoint_extension
            ? `${values.email} — extension ${created.sip_endpoint_extension}`
            : values.email,
        );
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the user.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Changing a role or password signs this person out of every session."
              : "They get a phone with their account, and can sign in as soon as you save."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" htmlFor="email" required error={form.formState.errors.email?.message}>
                <Input
                  type="email"
                  autoComplete="off"
                  invalid={Boolean(form.formState.errors.email)}
                  {...form.register("email")}
                />
              </Field>
              <Field
                label="Display name"
                htmlFor="display_name"
                hint="The name shown on their calls, here and on the far end."
              >
                <Input autoComplete="off" {...form.register("display_name")} />
              </Field>
            </div>

            <Field
              label="Role"
              htmlFor="role"
              required
              hint={ROLE_DESCRIPTIONS[role]}
              error={form.formState.errors.role?.message}
            >
              <Select
                value={role}
                onValueChange={(v) => form.setValue("role", v as Role, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r} description={ROLE_DESCRIPTIONS[r]}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {needsDomain && (
              <Field
                label="Domain"
                htmlFor="domain_id"
                required
                hint="The SIP realm their phone registers in."
                error={form.formState.errors.domain_id?.message}
              >
                <Select
                  value={form.watch("domain_id")?.toString() ?? ""}
                  onValueChange={(v) =>
                    form.setValue("domain_id", Number(v), { shouldValidate: true })
                  }
                >
                  <SelectTrigger invalid={Boolean(form.formState.errors.domain_id)}>
                    <SelectValue placeholder="Choose a domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {(domains ?? []).map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <PhoneSection
              form={form}
              domains={domains ?? []}
              editing={editing}
              hasPhone={hasPhone}
              needsDomain={needsDomain}
              canHavePhone={canHavePhone}
              renaming={renaming}
              currentDomainName={user?.sip_endpoint_domain_name ?? null}
            />

            <Field
              label={editing ? "Set a new password" : "Password"}
              htmlFor="password"
              required={!editing}
              hint={
                editing
                  ? "Leave blank to keep the current password."
                  : `At least ${MIN_PASSWORD} characters. Share it over something other than email.`
              }
              error={form.formState.errors.password?.message}
              action={
                <button
                  type="button"
                  className="text-xs text-accent-11 hover:underline"
                  onClick={() =>
                    form.setValue("password", generatePassphrase(), { shouldValidate: true })
                  }
                >
                  Generate
                </button>
              }
            >
              <Input
                // `text`, not `password`: whoever creates the account has to read this out or
                // paste it somewhere, and a masked field they cannot check is where typos and
                // support tickets come from.
                type="text"
                autoComplete="new-password"
                className="font-mono"
                invalid={Boolean(form.formState.errors.password)}
                {...form.register("password")}
              />
            </Field>

            {editing && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <div>
                  <p className="text-xs font-medium text-text">Active</p>
                  <p className="text-xs text-text-muted">
                    Turning this off signs them out immediately and blocks new logins.
                  </p>
                </div>
                <Switch
                  checked={form.watch("is_active")}
                  onCheckedChange={(v) => form.setValue("is_active", v)}
                  aria-label="Active"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The account's phone: where it lives, what it is called, and whether it records.
 *
 * Separated out because it is the one part of this form whose fields appear and disappear on
 * two independent conditions — whether the account already has a phone, and whether its role
 * needs somewhere to be told to put one — and reading that inline was three nested ternaries.
 */
function PhoneSection({
  form, domains, editing, hasPhone, needsDomain, canHavePhone, renaming, currentDomainName,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  domains: { id: number; name: string }[];
  editing: boolean;
  hasPhone: boolean;
  needsDomain: boolean;
  canHavePhone: boolean;
  renaming: boolean;
  currentDomainName: string | null;
}) {
  return (
    <div className="space-y-4 rounded-md border border-border p-3">
      <div>
        <p className="text-xs font-medium text-text">Phone</p>
        <p className="text-xs text-text-muted">
          {hasPhone
            ? `Their endpoint on ${currentDomainName ?? "the switch"}. It is theirs for as long as the account exists.`
            : editing
              ? "This account has no phone yet."
              : "Created with the account. Nobody else can be given it."}
        </p>
      </div>

      {!needsDomain && !hasPhone && (
        <Field
          label="Phone domain"
          htmlFor="endpoint_domain_id"
          hint="An admin spans every domain, so their phone has to be placed in one. Leave it unset for an account that never takes calls."
        >
          <Select
            value={form.watch("endpoint_domain_id")?.toString() ?? ""}
            onValueChange={(v) => form.setValue("endpoint_domain_id", Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="No phone" />
            </SelectTrigger>
            <SelectContent>
              {domains.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <Field
        label="Extension"
        htmlFor="extension"
        hint={
          !canHavePhone
            ? "Choose a domain above first — an extension has to live in a SIP realm."
            : hasPhone
              ? "Four digits. What colleagues dial, and what their softphone signs in as."
              : "Four digits. Leave blank for the next free number in the domain."
        }
        error={form.formState.errors.extension?.message}
      >
        <Input
          className="font-mono"
          autoComplete="off"
          inputMode="numeric"
          placeholder={canHavePhone ? "1004" : "—"}
          disabled={!canHavePhone}
          invalid={Boolean(form.formState.errors.extension)}
          {...form.register("extension")}
        />
      </Field>

      {renaming && (
        <Callout tone="warning">
          Changing the extension signs their softphone back in on the new number, and any desk
          phone on the old one stops until it is reconfigured. Calls they made before the change
          stay filed under the old number.
        </Callout>
      )}

      {hasPhone && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-text">Record calls</p>
            <p className="text-xs text-text-muted">
              {/* Both directions, because that is the part people get wrong: the toggle is not
                  "record what they dial", it is "record this phone". */}
              This phone's own side of calls it places and calls it is rung for. Takes effect on
              the next call
              {/* Only an admin's own Account page has this switch. For anybody below, this form
                  is the one place it can be changed, and the hint must not send them looking. */}
              {editsOwnPhone(form.watch("role"))
                ? " — and as an admin they can also change it in their own settings."
                : "."}
            </p>
          </div>
          <Switch
            checked={form.watch("record_calls")}
            onCheckedChange={(v) => form.setValue("record_calls", v)}
            aria-label="Record calls"
          />
        </div>
      )}
    </div>
  );
}
